/**
 * Centralized logging utility for the application
 * Provides different log levels and automatically handles production vs development environments
 */

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: any;
  timestamp: string;
  component?: string;
}

class Logger {
  private isDevelopment = process.env.NODE_ENV === 'development';
  private isProduction = process.env.NODE_ENV === 'production';

  private formatMessage(level: LogLevel, message: string, data?: any, component?: string): LogEntry {
    return {
      level,
      message,
      data: this.sanitizeData(data),
      timestamp: new Date().toISOString(),
      component
    };
  }

  private sanitizeData(data: any): any {
    if (!data) return data;

    // Remove sensitive information
    const sensitiveKeys = ['token', 'password', 'authorization', 'refresh_token', 'access_token'];

    if (typeof data === 'object') {
      const sanitized = { ...data };
      sensitiveKeys.forEach(key => {
        if (sanitized[key]) {
          sanitized[key] = '[REDACTED]';
        }
      });
      return sanitized;
    }

    return data;
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.isDevelopment) return true;
    if (this.isProduction) {
      // In production, only log warnings and errors
      return level === LogLevel.WARN || level === LogLevel.ERROR;
    }
    return true;
  }

  private log(level: LogLevel, message: string, data?: any, component?: string): void {
    if (!this.shouldLog(level)) return;

    const entry = this.formatMessage(level, message, data, component);

    switch (level) {
      case LogLevel.DEBUG:
        console.log(`🔍 [${component || 'APP'}] ${message}`, entry.data);
        break;
      case LogLevel.INFO:
        console.info(`ℹ️ [${component || 'APP'}] ${message}`, entry.data);
        break;
      case LogLevel.WARN:
        console.warn(`⚠️ [${component || 'APP'}] ${message}`, entry.data);
        break;
      case LogLevel.ERROR:
        console.error(`❌ [${component || 'APP'}] ${message}`, entry.data);
        break;
    }
  }

  debug(message: string, data?: any, component?: string): void {
    this.log(LogLevel.DEBUG, message, data, component);
  }

  info(message: string, data?: any, component?: string): void {
    this.log(LogLevel.INFO, message, data, component);
  }

  warn(message: string, data?: any, component?: string): void {
    this.log(LogLevel.WARN, message, data, component);
  }

  error(message: string, data?: any, component?: string): void {
    this.log(LogLevel.ERROR, message, data, component);
  }

  // Special method for API requests (only in development)
  apiRequest(url: string, method: string, data?: any): void {
    if (this.isDevelopment) {
      this.debug(`API Request: ${method} ${url}`, data, 'API');
    }
  }

  // Special method for API responses (only in development)
  apiResponse(url: string, status: number, data?: any): void {
    if (this.isDevelopment) {
      this.debug(`API Response: ${status} ${url}`, data, 'API');
    }
  }

  // Special method for API errors (always log)
  apiError(url: string, status: number, error: any): void {
    this.error(`API Error: ${status} ${url}`, error, 'API');
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const logDebug = (message: string, data?: any, component?: string) =>
  logger.debug(message, data, component);
export const logInfo = (message: string, data?: any, component?: string) =>
  logger.info(message, data, component);
export const logWarn = (message: string, data?: any, component?: string) =>
  logger.warn(message, data, component);
export const logError = (message: string, data?: any, component?: string) =>
  logger.error(message, data, component);
